import {ActionContext, ActionTree} from 'vuex';
import {AdminState} from '@/store/modules/Admin/state';
import {RootState} from '@/store';
import {_axios} from '@/plugins/axios';
import {AdminMessage, AdminMessageCreation, AdminMessageListAPI, AdminUserMessageListAPI} from '#/Conversations';
import i18n from '@/plugins/i18n';
import {AxiosResponse} from 'axios';
import {AdminMessageType} from '@/store/modules/Conversations/state';
import {StatsAPI} from '#/Stats';

export const actions: ActionTree<AdminState, RootState> = {
    createAdminMessage: (
        {dispatch}: ActionContext<AdminState, RootState>,
        newMessage: AdminMessageCreation,
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            _axios.post('admin-messages/', newMessage)
                  .then(() => {
                      dispatch(
                          'displaySnackBar',
                          {
                              text: i18n.t('admin.message.snackbar') as string,
                              type: 'valid',
                          },
                      )
                          .then(() => {
                              resolve();
                          });
                  })
                  .catch(() => {
                      reject();
                  });
        });
    },

    loadNewAdminMessages: (
        {commit}: ActionContext<AdminState, RootState>,
        payload: { isDetail?: boolean, types?: AdminMessageType[] } | null = null,
    ): Promise<number[]> => {
        return new Promise(resolve => {
            let url = 'admin-messages/unread/';

            if (payload?.types?.length !== 0) {
                payload?.types?.forEach((type, i) => {
                    url += `${i === 0 ? '?' : '&'}type=${type}`;
                });
            }
            _axios.get(url)
                  .then((response: AxiosResponse<AdminMessageListAPI>) => {
                      commit('LOAD_NEW_ADMIN_MESSAGES', response.data);
                      resolve(payload?.isDetail ? response.data.results.map(message => message.id) : []);
                  });
        });
    },

    loadPrivateAdminMessages: (
        {commit}: ActionContext<AdminState, RootState>,
        isDetail: boolean,
    ): Promise<number[]> => {
        return new Promise(resolve => {
            Promise.all([
                _axios.get('admin-messages/?type=' + AdminMessageType.MESSAGE),
                _axios.get('user-messages/'),
            ])
                   .then((responses: [AxiosResponse<AdminMessageListAPI>, AxiosResponse<AdminUserMessageListAPI>]) => {
                       const messages = [
                           ...responses[0].data.results,
                           ...responses[1].data.results,
                       ];
                       commit('LOAD_ADMIN_MESSAGES', messages);

                       if (isDetail) {
                           const authorAdmin = {
                               id: 0,
                               name: 'Admin',
                               profileColor: '',
                               profilePhoto: null,
                               profileUrl: null,
                           };
                           commit('GET_CONVERSATION', {
                               advert: {
                                   id: 0,
                                   title: 'Admin',
                               },
                               messages: messages.map(message => {
                                   const {sentAt, ...restMessage} = message;
                                   return {
                                       ...restMessage,
                                       author: authorAdmin,
                                       readAt: null,
                                       recipient: authorAdmin,
                                       sentAt: new Date(sentAt),
                                   };
                               }),
                           }, {root: true});
                       }

                       const adminUserMessageUnreadIds: number[] = [];

                       responses[1].data.results.forEach(message => {
                           if (message.readAt === null) adminUserMessageUnreadIds.push(message.id);
                       });
                       resolve(adminUserMessageUnreadIds);
                   });
        });
    },

    loadStats: ({commit}: ActionContext<AdminState, RootState>): Promise<void> => {
        return new Promise(resolve => {
            _axios.get('stats/')
                  .then((response: AxiosResponse<StatsAPI>) => {
                      commit('LOAD_STATS', response.data);
                      resolve();
                  });
        });
    },


    sendAcknowledgement: ({dispatch}: ActionContext<AdminState, RootState>, {id}: AdminMessage): Promise<void> => {
        return new Promise(resolve => {
            _axios.post(`admin-messages/${id}/acknowledge/`)
                  .then(() => {
                      dispatch('loadNewAdminMessages', {})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    sendUserMessage: (
        _: ActionContext<AdminState, RootState>,
        {adminId, authorId, message}: { adminId: string, authorId: string, message: string },
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            _axios.post(`user-messages/`, {
                author: adminId,
                recipient: authorId,
                content: message,
            })
                  .then(() => {
                      resolve();
                  })
                  .catch(() => {
                      reject();
                  });
        });
    },

    sendUserMessageMarkRead: (_: ActionContext<AdminState, RootState>, messageId: number): Promise<void> => {
        return new Promise((resolve, reject) => {
            _axios.patch(`user-messages/${messageId}/mark-read/`)
                  .then(() => {
                      resolve();
                  })
                  .catch(() => {
                      reject();
                  });
        });
    },
};
